import { addScheduledMessageButtonActions } from '@/common/reducers/AddScheduledMessageButtonReducer';
import { useGetLinkPreviewMutation, useGetSignedUploadUrlMutation } from '@/common/reducers/backend-api/backend-api';
import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { blobMap } from '@/common/services/blob-service';
import { normalizeFileMetadata } from '@common-services/file-utils';
import { uploadFilePut } from '@common-services/utils'
import WSButton from '@common-components/ws-button/ws-button';
import { setAsset, setImageId, setLinkPreview, setSendLinkPreview, setSnackbarMessage } from '@common/reducers/ScheduledMessageEditorReducer';
import type { Asset } from '@common/types/interface';
import WebIcon from '@mui/icons-material/Web';
import { CircularProgress, Stack, SxProps, Theme } from '@mui/material';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

/**
 * Decodes HTML entities in a string
 */
function decodeHtmlEntities(text: string): string {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// URL validation regex - matches http/https URLs
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;

const BUTTON_STYLES = {
  base: {
    textTransform: 'none' as const,
    letterSpacing: 0,
    fontWeight: 600,
    fontSize: '14px',
    padding: '4px 12px',
    width: '200px',
    height: '30px',
  },
  // The solid brand button pair, not the date picker's accent pair: that one
  // carries a navy label (white on #53bdeb is 2.13:1), and this label is white.
  active: {
    color: 'var(--fab-text) !important',
    backgroundColor: 'var(--fab-solid-bg)',
    '&:hover': {
      backgroundColor: 'var(--fab-solid-hover-bg)',
    }
  },
  inactive: {
    color: 'var(--fab-text) !important',
    backgroundColor: 'var(--fab-solid-bg) !important',
    '&:hover': {
      backgroundColor: 'var(--fab-solid-hover-bg) !important',
    }
  },
  disabled: {
    color: 'var(--color-text-muted) !important',
    backgroundColor: 'var(--color-surface-muted) !important',
    opacity: 0.5,
    cursor: 'not-allowed !important',
    '&:hover': {
      backgroundColor: 'var(--color-surface-muted) !important',
    }
  },
} as const;

const ICON_STYLES = {
  icon: {
    fontSize: 18
  },
  loadingSpinner: {
    // position: 'absolute' as const,
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    color: 'currentColor',
  },
  container: {
    position: 'relative' as const,
    color: 'inherit !important',
  }
} as const;

const ButtonContent = memo(function ButtonContent({
  buttonText,
  isLoading,
}: {
  buttonText: string;
  isLoading: boolean;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={ICON_STYLES.container}>
      {!isLoading &&
        <>
          <WebIcon sx={ICON_STYLES.icon} />
          <span>{buttonText}</span>
        </>
      }
      {isLoading && (
        <CircularProgress
          size={16}
          sx={ICON_STYLES.loadingSpinner}
        />
      )}
    </Stack>
  );
});

const getButtonStyles = (isPreviewEnabled: boolean, isDisabled: boolean): SxProps<Theme> => ({
  ...BUTTON_STYLES.base,
  ...(isDisabled
    ? BUTTON_STYLES.disabled
    : (isPreviewEnabled ? BUTTON_STYLES.active : BUTTON_STYLES.inactive)
  )
});

const LinkPreviewButton = memo(function LinkPreviewButton() {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const linkPreviewLoading = useAppSelector(state => state.AddScheduledMessageButtonReducer.linkPreviewLoading);
  const message = useAppSelector(state => state.ScheduledMessageEditorReducer.message);
  const sendLinkPreview = useAppSelector(state => state.ScheduledMessageEditorReducer.sendLinkPreview);
  const imageId = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.imageId);
  const asset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.asset);

  const [getLinkPreview] = useGetLinkPreviewMutation();
  const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();

  // Check if message contains a valid URL
  const hasValidUrl = useMemo(() => {
    return message ? URL_REGEX.test(message) : false;
  }, [message]);

  const isPreviewEnabled = useMemo(() =>
    hasValidUrl,
    [hasValidUrl]
  );

  // Button should be disabled only if no valid URL
  const isButtonDisabled = useMemo(() =>
    !hasValidUrl && !sendLinkPreview,
    [hasValidUrl]
  );

  const updateLinkPreview = useCallback(async () => {
    dispatch(addScheduledMessageButtonActions.setLinkPreviewLoading(true));
    
    // Extract the first URL from the message
    const urlMatch = message?.match(URL_REGEX);
    const link = urlMatch ? urlMatch[0] : null;
    
    if (link) {
      try {
        const response = await getLinkPreview({ link }).unwrap();
        if (response) {
          // Add proper base64 image prefix if thumbnail exists AND decode HTML entities
          const linkPreview = {
            ...response,
            title: response.title ? decodeHtmlEntities(response.title) : response.title,
            description: response.description ? decodeHtmlEntities(response.description) : response.description,
            siteName: response.siteName ? decodeHtmlEntities(response.siteName) : response.siteName,
            matchedText: response.matchedText ? decodeHtmlEntities(response.matchedText) : response.matchedText,
            thumbnail: response.thumbnail ? `data:image/jpeg;base64,${response.thumbnail}` : '',
            thumbnailHQ: response.thumbnailHQ ? `data:image/jpeg;base64,${response.thumbnailHQ}` : ''
          };

          dispatch(setSendLinkPreview(true));
          dispatch(setLinkPreview(linkPreview));
        }
      } catch (error) {
        console.error('Failed to get link preview:', error);
        dispatch(setSnackbarMessage('Failed to get link preview'));
        dispatch(setSendLinkPreview(false));
        dispatch(setLinkPreview({}));
      }
    }
    dispatch(addScheduledMessageButtonActions.setLinkPreviewLoading(false));
  }, [dispatch, getLinkPreview, message])

  const handlePreviewToggle = useCallback(() => {
    // Don't proceed if no valid URL
    if (!hasValidUrl && !sendLinkPreview) {
      return;
    }

    const newState = sendLinkPreview
      ? {
        sendLinkPreview: false,
        linkPreview: {} as Parameters<typeof setLinkPreview>[0]
      }
      : {
        sendLinkPreview: true,
        linkPreview: undefined as Parameters<typeof setLinkPreview>[0] | undefined
      };
    if (newState.linkPreview !== undefined) {
      dispatch(setLinkPreview(newState.linkPreview));
    }
    dispatch(setSendLinkPreview(newState.sendLinkPreview));

    if (newState.sendLinkPreview && message) {
      updateLinkPreview();
    }
  }, [sendLinkPreview, message, updateLinkPreview, dispatch, hasValidUrl]);


  const handleAssetChange = useCallback((asset?: Asset, imageId?: string) => {
    dispatch(setAsset(asset));
    dispatch(setImageId(imageId ?? ''));
  }, [dispatch]);

  // Initialize asset from imageId by uploading to storage (fallback to blob URL on error)
  useEffect(() => {
    const initFromImageId = async () => {
      if (!imageId) return;

      // If asset already has a real (non-blob) URL, do nothing
      const hasRealUrl = asset?.url && typeof asset.url === 'string' && !asset.url.startsWith('blob:');
      if (hasRealUrl) return;

      const image = blobMap?.get(imageId);
      if (!image) return;

      const normalizedMetadata = normalizeFileMetadata({
        originalName: image.name,
        mimeType: image.type,
      });
      const uploadMimeType = normalizedMetadata.type ?? image.type ?? 'application/octet-stream';

      try {
        const { url, publicUrl } = await getSignedUploadUrl(uploadMimeType).unwrap();
        await uploadFilePut(image, url);
        handleAssetChange({
          url: publicUrl,
          name: normalizedMetadata.name,
          type: normalizedMetadata.type ?? uploadMimeType,
        }, imageId);
      } catch (e) {
        // Fallback to a temporary blob URL so the UI still shows the image
        handleAssetChange({
          url: URL.createObjectURL(image),
          name: normalizedMetadata.name,
          type: normalizedMetadata.type ?? uploadMimeType,
        }, imageId);
      }
    };

    initFromImageId();
  }, [handleAssetChange, imageId, asset?.url, getSignedUploadUrl])

  // WhatsApp Business Rule: Mutual exclusion between link preview and file attachment
  useEffect(() => {
    const hasLinkPreview = !!sendLinkPreview;
    const hasAsset = !!asset?.url;
    const hasImageId = !!imageId;

    if (hasLinkPreview && (hasAsset || hasImageId)) {
      handleAssetChange(undefined, undefined);
      dispatch(setSnackbarMessage('File attachment removed - WhatsApp only supports one attachment type per message'));
    }
  }, [sendLinkPreview, asset?.url, imageId, handleAssetChange, dispatch]);

  const buttonText = useMemo(() => {
    if (!hasValidUrl && !sendLinkPreview) {
      return t('messageScheduling.noValidUrl');
    }
    return sendLinkPreview
      ? t('linkPreview.cancel')
      : t('linkPreview.update');
  }, [sendLinkPreview, hasValidUrl, t]);

  const buttonStyles = useMemo(() =>
    getButtonStyles(isPreviewEnabled, isButtonDisabled),
    [isPreviewEnabled, isButtonDisabled]
  );


  return (
    <Stack direction="row" spacing={1}>
      <WSButton
        onClick={handlePreviewToggle}
        disabled={isButtonDisabled}
        sx={buttonStyles}
      >
        <ButtonContent
          buttonText={buttonText}
          isLoading={linkPreviewLoading}
        />
      </WSButton>
    </Stack>
  );
});

export { LinkPreviewButton };

